/**
 * Functions for performing fetch calls with an authorization
 * @module
 */
import Flags from "common/constants/flags";
import { gzipCompressString } from "common/utils/compressionBrowser";
import { fetchWithTimeout, fetchWithTimeoutAndRetry, isUnauthorizedResponse } from "common/utils/fetchHelpers";
import { SSO_REFRESH_TOKEN_KEY } from "lib/cognito";
import { getToken } from "lib/ctx";
import { loginPath } from "lib/routeHelpers";

// These functions should be kept separate from the more generic
// `fetchWithTimeout` which may be invoked server side.

export const DEPLOYMENT_ID_MISMATCH = "x-deployment-id-mismatch";

/**
 * Adds "middleware" to window.fetch by intercepting the response
 * This is loaded at the beginning of _app.tsx
 *
 * Also hosts the auth failure breaker below: the middleware cannot tell a
 * terminal 401 from one the retry is about to resolve, so counting is fed
 * from the retry helpers.
 */
export async function loadClientFetchMiddleware() {
  if (typeof window === "undefined") {
    return;
  }

  const { fetch: originalFetch } = window;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    // If a server response includes deployment mismatch, dispatch an event so
    // the app shell can reload onto the fresh bundle (consumed in main.tsx).
    const deploymentIdMismatch = response.headers.get(DEPLOYMENT_ID_MISMATCH);
    if (deploymentIdMismatch && Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE) {
      const event = new CustomEvent(DEPLOYMENT_ID_MISMATCH, { detail: deploymentIdMismatch });
      window.dispatchEvent?.(event);
    }

    return response;
  };
}

// Circuit breaker for dead sessions: once the threshold of terminal auth
// failures is reached within the window, further authorized requests fail
// fast without reaching the server and the tab is redirected to login.
// Only terminal failures count — transient ones are resolved by the retry.
const AUTH_FAILURE_THRESHOLD = 5;
const AUTH_FAILURE_WINDOW_MS = 2 * 60 * 1000;

/** sessionStorage marker set when the breaker redirects to login. */
export const AUTH_FAILURE_REDIRECT_MARKER = "higharc-auth-failure-redirected";

let authFailureTimes: number[] = [];
let authFailureBreakerOpen = false;

/** Clears the marker set when the breaker redirected to login; called after a successful login. */
export function clearAuthFailureRedirectMarker(): void {
  try {
    sessionStorage.removeItem(AUTH_FAILURE_REDIRECT_MARKER);
  } catch {
    // An inaccessible marker cannot cause a redirect loop; nothing to clear.
  }
}

/** Resets module-level breaker state between unit tests. */
export function resetAuthFailureBreakerForTests(): void {
  authFailureTimes = [];
  authFailureBreakerOpen = false;
}

/**
 * Counts one terminal auth failure toward the breaker and trips it at the
 * threshold. A `no-fresh-token` failure only counts when the SSO refresh
 * token is gone (Cognito's `invalid_grant` wipe) — a preserved token means
 * the failure was transient and the next refresh can recover. A
 * `resent-rejected` failure always counts: a fresh token the server just
 * rejected means the session is dead.
 */
function recordAuthFailure(reason: "no-fresh-token" | "resent-rejected"): void {
  if (authFailureBreakerOpen) {
    return;
  }

  if (reason === "no-fresh-token" && isSsoRefreshTokenPresent()) {
    return;
  }

  const now = Date.now();
  authFailureTimes = authFailureTimes.filter((time) => now - time < AUTH_FAILURE_WINDOW_MS);
  authFailureTimes.push(now);

  if (authFailureTimes.length >= AUTH_FAILURE_THRESHOLD) {
    tripAuthFailureBreaker();
  }
}

/**
 * False when localStorage is unavailable, so the failure counts as terminal —
 * the request flood is the bigger risk, and storage failures are rare.
 */
function isSsoRefreshTokenPresent(): boolean {
  try {
    return localStorage.getItem(SSO_REFRESH_TOKEN_KEY) !== null;
  } catch {
    return false;
  }
}

function tripAuthFailureBreaker(): void {
  // Write the marker before opening the breaker: if the write fails, stay
  // closed so the tab keeps a path to recovery instead of bricking silently.
  try {
    if (sessionStorage.getItem(AUTH_FAILURE_REDIRECT_MARKER)) {
      return;
    }
    sessionStorage.setItem(AUTH_FAILURE_REDIRECT_MARKER, "true");
  } catch {
    return;
  }

  authFailureBreakerOpen = true;
  authFailureTimes = [];

  window.location.assign(loginPath(window.location.pathname + window.location.search));
}

/**
 * Synthetic 401 returned/thrown once the breaker has tripped, so further
 * authorized requests fail fast without reaching the server.
 */
function authFailureBreakerResponse(): Response {
  return new Response(JSON.stringify({ message: "Your session has expired. Please sign in again." }), {
    status: 401,
    statusText: "Unauthorized",
  });
}

// Generate client headers with or without auth
export async function getFetchHeaders(withAuth: boolean, customHeaders: Record<string, string> = {}) {
  if (withAuth && authFailureBreakerOpen) {
    // Short-circuit before getToken runs the Cognito refresh path.
    throw authFailureBreakerResponse();
  }
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Deployment-ID": Flags.GIT_COMMIT_HASH,
    ...customHeaders,
  };

  if (withAuth) {
    const token = await getToken();
    if (token) {
      headers["authorization"] = token;
    }
  }

  return headers;
}

/**
 * Runs `attempt` and, on a 401, force-refreshes the token and resends the
 * request with it exactly once via `resendWithToken`. A 401 from that second
 * run is not retried again — it propagates to the caller. Terminal outcomes
 * feed the auth failure breaker; 401s the retry resolves never count.
 */
async function withRetryOn401(
  attempt: () => Promise<Response>,
  resendWithToken: (freshToken: string) => Promise<Response>,
): Promise<Response> {
  // Checked here as well as in the header builders: their check runs before
  // getToken() resolves, so the breaker can trip mid-flight — this is the
  // last point that keeps the request off the network.
  if (authFailureBreakerOpen) {
    throw authFailureBreakerResponse();
  }

  try {
    return await attempt();
  } catch (error) {
    if (!isUnauthorizedResponse(error)) {
      throw error;
    }

    const freshToken = await getToken({ force: true });
    if (!freshToken) {
      recordAuthFailure("no-fresh-token");
      throw error;
    }

    try {
      return await resendWithToken(freshToken);
    } catch (retryError) {
      if (isUnauthorizedResponse(retryError)) {
        recordAuthFailure("resent-rejected");
      }
      throw retryError;
    }
  }
}

async function fetchWithAuthRetry(path: string, init: RequestInit, timeout?: number): Promise<Response> {
  return withRetryOn401(
    () => fetchWithTimeout(path, init, timeout),
    (freshToken) => {
      const headers = new Headers(init.headers);
      headers.set("authorization", freshToken);

      return fetchWithTimeout(path, { ...init, headers }, timeout);
    },
  );
}

/** Drop-in `fetch` that merges auth headers from {@link getFetchHeaders}. */
export const authFetch: typeof fetch = async (input, init) => {
  // Unlike the timeout helpers, authFetch must keep the fetch contract of
  // resolving with a Response even when the breaker is open.
  if (authFailureBreakerOpen) {
    return authFailureBreakerResponse();
  }

  const baseHeaders = await getFetchHeaders(true);
  const merged = new Headers(baseHeaders);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      merged.set(key, value);
    });
  }

  const response = await fetch(input, { ...init, headers: merged });

  // Raw fetch surfaces 401s as a Response instead of throwing, so the retry
  // logic is mirrored here. A Request input with an already-consumed body
  // cannot be resent.
  if (response.status === 401 && !(input instanceof Request && input.bodyUsed)) {
    const freshToken = await getToken({ force: true });
    if (!freshToken) {
      recordAuthFailure("no-fresh-token");
      return response;
    }

    merged.set("authorization", freshToken);
    const retriedResponse = await fetch(input, { ...init, headers: merged });
    if (retriedResponse.status === 401) {
      recordAuthFailure("resent-rejected");
    }
    return retriedResponse;
  }

  return response;
};

export async function getWithTimeout(path: string): Promise<Response> {
  return fetchWithAuthRetry(path, {
    method: "GET",
    headers: await getFetchHeaders(true),
  });
}

export async function getWithTimeoutAndRetry(path: string, retries?: number, timeoutMs?: number): Promise<Response> {
  const headers = await getFetchHeaders(true);

  // fetchWithTimeoutAndRetry rethrows 401s immediately (they are deterministic
  // for the current token), so the forced-refresh resend below is the only
  // retry a 401 gets; network errors and 5xx keep their normal retry budget.
  return withRetryOn401(
    () => fetchWithTimeoutAndRetry(path, { method: "GET", headers }, retries, timeoutMs),
    (freshToken) =>
      fetchWithTimeoutAndRetry(
        path,
        { method: "GET", headers: { ...headers, authorization: freshToken } },
        retries,
        timeoutMs,
      ),
  );
}

export async function putWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return putWithTimeoutNoStringify({ path, body: JSON.stringify(body), timeout });
}

export async function putWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);

  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithAuthRetry(
    path,
    {
      method: "PUT",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function patchWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);

  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithAuthRetry(
    path,
    {
      method: "PATCH",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function putFormDataWithTimeout(path: string, body: File | FormData, timeout?: number): Promise<Response> {
  const headers = await buildHeadersAddAuthIfPossible();

  return fetchWithAuthRetry(
    path,
    {
      method: "PUT",
      body,
      headers,
    },
    timeout,
  );
}

async function buildHeadersAddAuthIfPossible(): Promise<HeadersInit> {
  if (authFailureBreakerOpen) {
    throw authFailureBreakerResponse();
  }
  const authorization = await getToken();

  const headers = {
    "X-Deployment-ID": Flags.GIT_COMMIT_HASH,
  };

  if (authorization) {
    headers["authorization"] = authorization;
  }

  return headers;
}

export async function patchWithTimeout<Body = any>(path: string, body: Body, timeout?: number): Promise<Response> {
  return fetchWithAuthRetry(
    path,
    {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: await getFetchHeaders(true),
    },
    timeout,
  );
}

export async function postFormDataWithTimeout(
  path: string,
  body: File | FormData,
  timeout?: number,
): Promise<Response> {
  const headers = await buildHeadersAddAuthIfPossible();

  return fetchWithAuthRetry(
    path,
    {
      method: "POST",
      body,
      headers,
    },
    timeout,
  );
}

export async function postWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return postWithTimeoutNoStringify({ path, body: JSON.stringify(body), timeout });
}

export async function postWithTimeoutNoStringify({
  path,
  body,
  timeout,
  compressBody = false,
}: {
  path: string;
  body: string;
  timeout?: number;
  compressBody?: boolean;
}): Promise<Response> {
  const headers = await getFetchHeaders(true);
  let finalBody: string | Uint8Array<ArrayBuffer> = body;

  if (compressBody) {
    finalBody = await gzipCompressString(body);

    headers["Content-Encoding"] = "gzip";
  }

  return fetchWithAuthRetry(
    path,
    {
      method: "POST",
      body: finalBody,
      headers,
    },
    timeout,
  );
}

export async function postWithTimeoutNoAuth<Body = any>(
  path: string,
  body?: Body,
  timeout?: number,
): Promise<Response> {
  return fetchWithTimeout(
    path,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: await getFetchHeaders(false),
    },
    timeout,
  );
}

export async function deleteWithTimeout<Body = any>(path: string, body?: Body, timeout?: number): Promise<Response> {
  return fetchWithAuthRetry(
    path,
    {
      method: "DELETE",
      body: body ? JSON.stringify(body) : "",
      headers: await getFetchHeaders(true),
    },
    timeout,
  );
}

export interface FileUploadWithTimeoutParams {
  path: string;
  fileName: string;
  file: File;
  timeout?: number;
  body?: Record<string, any>;
}

export async function fileUploadWithTimeout({
  path,
  fileName,
  file,
  timeout,
  body = {},
}: FileUploadWithTimeoutParams): Promise<Response> {
  const formData = new FormData();
  formData.append(fileName, file);

  for (const param in body) {
    formData.append(param, JSON.stringify(body[param]));
  }

  const headers = await buildHeadersAddAuthIfPossible();

  headers["Accept"] = "application/json";

  return fetchWithAuthRetry(
    path,
    {
      method: "POST",
      body: formData,
      headers,
    },
    timeout,
  );
}
